import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import type { ReactNode } from 'react'
import { LumpsumAllocationCategoryExecutionStatus } from '../../../api/allocation.types'
import {
  LumpsumAllocationSellReasonType,
  type LumpsumAllocationSellReason
} from '../../api/allocationRecommendation.types'
import {
  buildPlanPercentageGap,
  planPercentageLabel
} from '../../../../display-values/lumpsumPlanPercentage'
import { formatRiskGroupLabel } from '../../../../utils/allocationRiskGroupUtils'
import type { FundMissedGainsInfoParams } from '../missed-gains/FundMissedGainsInfoBottomSheet'
import type { PlanRecommendationPairView } from '../../pair/planRecommendationPair.types'
import { isMissedGainsComparisonAvailable } from '../../../plans-list/header/rebalance/MissedGainsComparisonBarGraph'
import {
  ReasonPointer,
  ReasonPointerHighlightStrip,
  ReasonPointerRankHighlightStrip
} from './ReasonPointerUi'

enum SellReasonExecutionScope {
  ALLOCATION = 'ALLOCATION',
  FUND_COUNT = 'FUND_COUNT'
}

function pickCategoryExecutionStatus(
  executionScope: SellReasonExecutionScope,
  categoryAllocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null,
  categoryFundCountExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
): LumpsumAllocationCategoryExecutionStatus | string | null {
  if (executionScope === SellReasonExecutionScope.ALLOCATION) {
    return categoryAllocationExecutionStatus ?? null
  }
  return categoryFundCountExecutionStatus ?? null
}

type SellReasonPointerData = {
  reason: LumpsumAllocationSellReason
  index: number
  pair: PlanRecommendationPairView
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

type SellReasonPointerWithExecutionScope = SellReasonPointerData & {
  executionScope: SellReasonExecutionScope
}

/** Allocation sell reason pointers always use warning tone. */

export function getSellReasonSubtitleLabel(reason: LumpsumAllocationSellReason): string {
  switch (reason?.reason) {
    case LumpsumAllocationSellReasonType.MISSED_GAINS:
      return 'Missed gains'
    case LumpsumAllocationSellReasonType.POOR_PERFORMANCE:
      return 'Poor Power Rank'
    case LumpsumAllocationSellReasonType.INSIGNIFICANT_ALLOCATION:
      return 'Insignificant allocation'
    case LumpsumAllocationSellReasonType.TOO_MANY_FUNDS:
      return 'Too many funds'
    case LumpsumAllocationSellReasonType.CATEGORY_NOT_RECOMMENDED:
      return 'Category not needed'
    case LumpsumAllocationSellReasonType.HIGH_EXPOSURE:
      return 'High exposure'
    case LumpsumAllocationSellReasonType.CONCENTRATION_RISK:
      return 'Concentration risk'
    case LumpsumAllocationSellReasonType.REALLOCATED_PREFERENCE:
      return 'Reallocated as per your preference'
    default:
      return ''
  }
}

export function SellReasonPointer(data: SellReasonPointerData): ReactNode {
  switch (data.reason?.reason) {
    case LumpsumAllocationSellReasonType.MISSED_GAINS: {
      return <MissedGainsSellReasonPointer {...data} />
    }
    case LumpsumAllocationSellReasonType.POOR_PERFORMANCE: {
      return <PoorPerformanceSellReasonPointer {...data} />
    }
    case LumpsumAllocationSellReasonType.INSIGNIFICANT_ALLOCATION: {
      return <InsignificantAllocationSellReasonPointer {...data} />
    }
    case LumpsumAllocationSellReasonType.TOO_MANY_FUNDS: {
      const executionScope = SellReasonExecutionScope.FUND_COUNT
      return <TooManyFundsSellReasonPointer {...data} executionScope={executionScope} />
    }
    case LumpsumAllocationSellReasonType.CATEGORY_NOT_RECOMMENDED: {
      return <CategoryNotRecommendedSellReasonPointer {...data} />
    }
    case LumpsumAllocationSellReasonType.HIGH_EXPOSURE: {
      const executionScope = SellReasonExecutionScope.ALLOCATION
      return <HighExposureSellReasonPointer {...data} executionScope={executionScope} />
    }
    case LumpsumAllocationSellReasonType.CONCENTRATION_RISK: {
      const executionScope = SellReasonExecutionScope.ALLOCATION
      return <ConcentrationRiskSellReasonPointer {...data} executionScope={executionScope} />
    }
    case LumpsumAllocationSellReasonType.REALLOCATED_PREFERENCE: {
      return <ReallocatedPreferenceSellReasonPointer {...data} />
    }
    default: {
      return null
    }
  }
}

export function MissedGainsSellReasonPointer({
  reason,
  index,
  pair,
  onMissedGainsInfoClick
}: SellReasonPointerData) {
  // Copy + missed-gains sheet use the held fund being left (pair exiting side).
  const currentFund = pair.exitingFund
  const missedGains = reason?.missedGains
  const currentValue = currentFund?.currentValue
  // Pair maps missing currentValue → 0; only show info when the comparison sheet can render.
  const canOpenMissedGainsInfo = isMissedGainsComparisonAvailable({
    missedGains,
    baselineValue: currentValue
  })

  return (
    <ReasonPointer
      index={index}
      title="Missed gains"
      subtitle="on your current fund"
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle={`+ ${formatRupeeLabel({ rawAmount: missedGains ?? 0 })}`}
          highlightDescription="Returns you missed by staying in your current fund instead of switching earlier"
          tone="warning"
          fundName={currentFund?.fundName ?? ''}
          fundIsin={currentFund?.isin ?? ''}
          onInfoClick={
            canOpenMissedGainsInfo
              ? () => {
                  onMissedGainsInfoClick({
                    missedGains: missedGains ?? 0,
                    currentValue: currentValue ?? 0,
                    planSnapshotDate: pair.planSnapshotDate
                  })
                }
              : undefined
          }
        />
      )}
    />
  )
}

export function PoorPerformanceSellReasonPointer({
  reason: _reason,
  index,
  pair
}: SellReasonPointerData) {
  const categoryName = pair.exitingFund.categoryName || 'this category'

  return (
    <ReasonPointer
      index={index}
      title="Poor Power Rank"
      subtitle="in its category"
      highlightContent={(
        <ReasonPointerRankHighlightStrip
          powerRank={pair.exitingFund.powerRank}
          powerRating={pair.exitingFund.powerRating}
          description={`This fund has been underperforming its benchmarks in ${categoryName}`}
          tone="warning"
        />
      )}
    />
  )
}

export function InsignificantAllocationSellReasonPointer({
  reason,
  index,
  pair
}: SellReasonPointerData) {
  const fundName = pair.exitingFund.fundName || 'this fund'
  const insignificantPercentage = reason?.insignificantCategoryAllocationPercentage ?? 0
  const highlightTitle =
    insignificantPercentage < 1
      ? '<1%'
      : planPercentageLabel({ rawPercentage: insignificantPercentage })

  return (
    <ReasonPointer
      index={index}
      title="Insignificant allocation"
      subtitle={`in ${fundName}`}
      highlightTitle={highlightTitle}
      highlightDescription="Allocation in this fund. Since you have better-performing funds in the same category, this fund is not needed."
      tone="warning"
    />
  )
}

export function TooManyFundsSellReasonPointer({
  reason,
  index,
  pair,
  executionScope
}: SellReasonPointerWithExecutionScope) {
  const categoryExecutionStatus = pickCategoryExecutionStatus(
    executionScope,
    pair.exitingFund.categoryAllocationExecutionStatus,
    pair.exitingFund.categoryFundCountExecutionStatus
  )
  const categoryName = pair.exitingFund.categoryName || 'this category'
  const idealCategoryFundCount = reason?.idealCategoryFundCount ?? 0
  const progressiveDescription = `Removing this fund helps consolidate to ${idealCategoryFundCount} funds in ${categoryName}.`
  const notStartedDescription = `Removing this fund helps consolidate from ${reason?.currentCategoryFundCount ?? 0} to ${idealCategoryFundCount} funds in ${categoryName}.`

  let highlightDescription: string
  if (pair.exitingFund.executed) {
    highlightDescription = progressiveDescription
  } else {
    if (categoryExecutionStatus === LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS) {
      highlightDescription = progressiveDescription
    } else {
      highlightDescription = notStartedDescription
    }
  }

  return (
    <ReasonPointer
      index={index}
      title="Too many funds"
      subtitle={`in ${categoryName}`}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle=""
          highlightDescription={highlightDescription}
          tone="warning"
        />
      )}
    />
  )
}

export function CategoryNotRecommendedSellReasonPointer({
  reason,
  index,
  pair
}: SellReasonPointerData) {
  const categoryName = pair.exitingFund.categoryName || 'this category'
  const riskGroupLabel = formatRiskGroupLabel(reason?.riskGroup)
  const idealCategoryCountInGroup = reason?.idealCategoryCountInGroup ?? 0

  return (
    <ReasonPointer
      index={index}
      title={`${categoryName} category not needed`}
      subtitle={riskGroupLabel ? `in ${riskGroupLabel}` : undefined}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle=""
          highlightDescription={`Removing this category helps consolidate to ${idealCategoryCountInGroup}${riskGroupLabel ? ` ${riskGroupLabel}` : ''} categories`}
          tone="warning"
        />
      )}
    />
  )
}

export function HighExposureSellReasonPointer({
  reason,
  index,
  pair,
  executionScope
}: SellReasonPointerWithExecutionScope) {
  const categoryExecutionStatus = pickCategoryExecutionStatus(
    executionScope,
    pair.exitingFund.categoryAllocationExecutionStatus,
    pair.exitingFund.categoryFundCountExecutionStatus
  )
  const categoryName = pair.exitingFund.categoryName || 'this category'
  const currentCategoryAllocationPercentage = reason?.currentCategoryAllocationPercentage ?? 0
  // Category ideal from the strategy. The concentration cap is a different field.
  const targetPercentage = reason?.idealCategoryAllocationPercentage ?? 0
  const targetLabel = planPercentageLabel({ rawPercentage: targetPercentage })
  const progressiveDescription = `Ideal allocation recommended in ${categoryName} is ${targetLabel}.\n\nTo reduce allocation in tax-optimised way, this is one of the suitable fund that has been identified.`

  let highlightTitle: string | undefined
  let highlightDescription: string

  if (pair.exitingFund.executed) {
    highlightDescription = progressiveDescription
  } else {
    if (categoryExecutionStatus === LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS) {
      highlightDescription = progressiveDescription
    } else {
      const exposureGap = buildPlanPercentageGap({
        fromPercentage: currentCategoryAllocationPercentage,
        toPercentage: targetPercentage,
        gapMode: 'roundEachThenSubtract'
      })
      highlightTitle = planPercentageLabel({ rawPercentage: currentCategoryAllocationPercentage })
      highlightDescription = `In ${categoryName}, Ideal allocation recommended is ${targetLabel}, so you need to reduce current exposure by ${exposureGap.displayPercentage.label}.\n\nTo reduce allocation in tax-optimized way, this is one of the suitable fund that has been identified.`
    }
  }

  return (
    <ReasonPointer
      index={index}
      title="High exposure"
      subtitle={`in ${categoryName}`}
      highlightTitle={highlightTitle}
      highlightDescription={highlightDescription}
      tone="warning"
    />
  )
}

export function ConcentrationRiskSellReasonPointer({
  reason,
  index,
  pair,
  executionScope
}: SellReasonPointerWithExecutionScope) {
  const categoryExecutionStatus = pickCategoryExecutionStatus(
    executionScope,
    pair.exitingFund.categoryAllocationExecutionStatus,
    pair.exitingFund.categoryFundCountExecutionStatus
  )
  const fundName = pair.exitingFund.fundName || 'this fund'
  // Per-fund cap. Backend sends this on the concentration reason.
  const highlightDescription = `For your portfolio, we recommend keeping each fund up to ${planPercentageLabel({ rawPercentage: reason?.highExposureTargetPercentage ?? 0 })} to avoid concentration risk.`

  let highlightTitle: string | undefined
  if (pair.exitingFund.executed) {
    highlightTitle = undefined
  } else {
    if (categoryExecutionStatus === LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS) {
      highlightTitle = undefined
    } else {
      highlightTitle = planPercentageLabel({ rawPercentage: reason?.currentCategoryAllocationPercentage ?? 0 })
    }
  }

  return (
    <ReasonPointer
      index={index}
      title="Concentration risk"
      subtitle={`in ${fundName}`}
      highlightTitle={highlightTitle}
      highlightDescription={highlightDescription}
      tone="warning"
    />
  )
}

export function ReallocatedPreferenceSellReasonPointer({
  reason: _reason,
  index,
  pair
}: SellReasonPointerData) {
  const categoryName = pair.exitingFund.categoryName || 'this category'

  return (
    <ReasonPointer
      index={index}
      title="Reallocated as per your preference"
      subtitle="your preference"
      highlightTitle={`${categoryName} reallocated to core`}
      highlightDescription="This fund category is being reallocated to your core portfolio based on your ideal allocation plan and preferences, in a tax-optimised way."
      tone="warning"
    />
  )
}
